import _get from 'lodash/get';
import _set from 'lodash/set';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as GeneralTypes from '@ai4/form-viewer/dist/types/general';
import { isValidDate } from 'src/helpers';
import { FileArgs, useFileRequest } from './useFileRequest';
import { useSchema } from './useSchema';

interface FormViewerArgs {
	file?: FileArgs;
}

type TransformMap = Record<string, boolean | string>;

export function useFormViewer(args?: FormViewerArgs) {
	const { file } = args || {};
	const { downloadFile, sendFile, getDownloadFileUrl } = useFileRequest(file || { domain: '', endpoint: '' });

	const transformData = useCallback((object: any, map: TransformMap) => {
		const pad = (n: number) => `${Math.floor(Math.abs(n))}`.padStart(2, '0');

		const fields = Object.keys(map);
		return fields.reduce((acc, field) => {
			let val = _get(object, field);
			switch (map[field]) {
				case 'date':
					if (isValidDate(val)) {
						const date = new Date(Date.parse(val));
						val = date.toISOString().split('T')[0];
					}
					if (val && val.length === 0) val = null;
					break;
				case 'datetime':
					if (isValidDate(val)) {
						const date = new Date(Date.parse(val));
						val =
							[date.getFullYear(), pad(date.getMonth() + 1), pad(date.getDate())].join('-') +
							'T' +
							[pad(date.getHours()), pad(date.getMinutes()), pad(date.getSeconds())].join(':');
					}
					if (val && val.length === 0) val = null;
					break;
				case 'number':
					val = parseFloat(val);
					break;
			}
			return _set(acc, field, val);
		}, {});
	}, []);

	const onSelectFiles = useCallback(async (args: GeneralTypes.OnSelectFilesArgs, override?: Partial<FileArgs>) => {
		const { field, files, setProgress, setSuccess, setError } = args;
		if (!file) return;
		const { name } = field[0];
		try {
			const res = await sendFile(files[0] as any, {
				...override,
				fieldName: name,
				onProgress: (file, progressEvent) => {
					setProgress(progressEvent.percent);
				},
			});
			setSuccess([
				{
					...files[0],
					uniqueId: res.fileUniqueId,
				},
			]);
		} catch (err) {
			setError([{ message: _get(err, 'data.messages[0]', '') }]);
		}
	}, []);

	const onDownloadFile = useCallback((args: GeneralTypes.OnDownloadFileArgs, override?: Partial<FileArgs>) => {
		const { field, file } = args;
		if (!file) return;
		const [input] = field;
		if (input.value && input.value.uniqueId) {
			const path = getDownloadFileUrl(file, {
				...override,
				fieldName: input.name,
			}).replace('{fileUniqueId}', input.value.uniqueId);
			return {
				onClick: (e: React.MouseEvent) => {
					downloadFile({ ...file, path });
					e.stopPropagation();
				},
			};
		}
		return undefined;
	}, []);

	const onRemoveFile = useCallback(async (args: GeneralTypes.OnRemoveFileArgs, override?: Partial<FileArgs>) => {
		const { field, file, setSuccess, setError } = args;
		if (!file) return;
		const [input, meta, helpers] = field;
		try {
			setSuccess();
		} catch (e) {
			setError([e as any]);
		}
	}, []);

	return {
		transformData,
		onSelectFiles,
		onDownloadFile,
		onRemoveFile,
	};
}

export const useMutationTransformer = (mutation: string) => {
	const [map, setMap] = useState<TransformMap>();
	const { mutations = [] } = useSchema();
	const { transformData } = useFormViewer();
	const { fields = [] } = mutations.find((m: any) => m.id === mutation) || {};

	useEffect(() => {
		if (fields.length === 0) return;
		const $map = fields.reduce<TransformMap>(
			(acc, field) => {
				let to: string | boolean = true;
				switch (field.type) {
					case 'date':
						to = 'date';
						break;
					case 'datetime':
						to = 'datetime';
						break;
					case 'int':
					case 'decimal':
						to = 'number';
						break;
				}
				return {
					...acc,
					[field.name]: to,
				};
			},
			{
				uniqueId: true,
			}
		);
		setMap($map);
	}, [mutations]);

	const transform = useCallback(
		(values) => {
			return transformData(values, map || {});
		},
		[map]
	);

	return map ? transform : undefined;
};
